import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { FIC_TYPES } from "@/lib/writing";
import { ChipInput } from "@/components/ChipInput";
import { FicDetails } from "@/components/FicDetails";

const sel = "h-10 w-full rounded-md bg-inset border border-line text-sm text-slate-200 px-3";
export const emptyWork = {
  title: "", kind: "original", fic_type: null, fic_type_other: "", summary: "", tags: [], world_id: null, profile_id: null,
  fandoms: [], rating: "not_rated", warnings: [], custom_warnings: [], characters: [], relationships: [], hiatus: false, font: "", page_size: "paperback",
};

const ProfileSelect = ({ worldId, value, onChange }) => {
  const [list, setList] = useState([]);
  useEffect(() => { api.get(`/worlds/${worldId}/fanon`).then((r) => setList(r.data.profiles)).catch(() => setList([])); }, [worldId]);
  return (
    <div className="space-y-1.5"><Label>Fanon profile <span className="text-slate-500 font-normal">· the version of the world this work uses</span></Label>
      <select value={value || ""} onChange={(e) => onChange(e.target.value || null)} className={sel} data-testid="work-profile-select">
        <option value="">Canon</option>
        {list.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      {list.length === 0 && <p className="text-[11px] text-slate-500" data-testid="work-profile-empty">This world has no fanon profiles yet. Create one from the world's Canon menu.</p>}
      {value && <Link to={`/worlds/${worldId}/fanon/${value}`} className="inline-block text-xs text-violet-300 hover:text-violet-200" data-testid="work-profile-open">Open this fanon profile →</Link>}
    </div>
  );
};

// Shared form for creating and editing a work
export const WorkForm = ({ value, onChange }) => {
  const [worlds, setWorlds] = useState([]);
  useEffect(() => { api.get("/worlds").then((r) => setWorlds(r.data)).catch(() => {}); }, []);
  const set = (k, v) => onChange({ ...value, [k]: v });
  const fan = value.kind === "fanfiction";
  return (
    <div className="space-y-4" data-testid="work-form">
      <div className="space-y-1.5"><Label>Title <span className="text-slate-500 font-normal">· leave blank for "Untitled"</span></Label>
        <Input value={value.title} maxLength={200} onChange={(e) => set("title", e.target.value)} placeholder="The Lighthouse Keeper's Daughter" data-testid="work-title-input" /></div>
      <div className="space-y-1.5"><Label>Kind of work</Label>
        <div className="grid grid-cols-2 gap-2" role="radiogroup">
          {[["original", "Original work"], ["fanfiction", "Fanfiction"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => onChange({ ...value, kind: k, fic_type: k === "fanfiction" ? value.fic_type || "longfic" : null })} aria-pressed={value.kind === k}
              className={`h-10 rounded-md border text-sm transition-colors ${value.kind === k ? (k === "fanfiction" ? "border-violet-400/60 bg-violet-500/10 text-violet-200" : "border-amber-500/60 bg-amber-500/10 text-amber-200") : "border-line text-slate-400 hover:text-slate-200"}`}
              data-testid={`work-kind-${k}`}>{l}</button>
          ))}
        </div></div>
      {fan && (
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1.5"><Label>Type</Label>
            <select value={value.fic_type || "longfic"} onChange={(e) => set("fic_type", e.target.value)} className={sel} data-testid="work-type-select">
              {FIC_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select></div>
          {value.fic_type === "other" && <div className="space-y-1.5"><Label>Your label</Label>
            <Input required value={value.fic_type_other} maxLength={40} onChange={(e) => set("fic_type_other", e.target.value)} placeholder="e.g. Songfic" data-testid="work-type-other-input" /></div>}
        </div>
      )}
      <div className="space-y-1.5"><Label>Summary</Label>
        <Textarea rows={3} value={value.summary} maxLength={3000} onChange={(e) => set("summary", e.target.value)} placeholder="What's it about?" data-testid="work-summary-input" /></div>
      <div className="space-y-1.5"><Label>Tags</Label><ChipInput values={value.tags} onChange={(t) => set("tags", t)} placeholder="Add a tag and press Enter" testId="work-tag" /></div>
      <div className="space-y-1.5"><Label>Linked world <span className="text-slate-500 font-normal">· optional, lets you type [[ to link wiki pages</span></Label>
        <select value={value.world_id || ""} onChange={(e) => onChange({ ...value, world_id: e.target.value || null, profile_id: null })} className={sel} data-testid="work-world-select">
          <option value="">No linked world</option>
          {worlds.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select></div>
      {value.world_id && <ProfileSelect worldId={value.world_id} value={value.profile_id} onChange={(v) => set("profile_id", v)} />}
      <FicDetails value={value} set={set} />
    </div>
  );
};

const DETAIL_KEYS = ["fandoms", "rating", "warnings", "custom_warnings", "characters", "relationships", "hiatus", "font", "page_size", "profile_id"];
export const workBody = (w) => ({
  title: w.title, kind: w.kind, fic_type: w.kind === "fanfiction" ? w.fic_type : null, fic_type_other: w.fic_type_other || "", summary: w.summary, tags: w.tags, world_id: w.world_id || null,
  ...Object.fromEntries(DETAIL_KEYS.map((k) => [k, w[k] ?? emptyWork[k]])),
});
